
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDownWideNarrow, ChevronLeft, ChevronRight, FileSearch2, X } from "lucide-react";
import BannerCard from "./BannerCard";
import Icon from "./Icon";
import KnowledgeFilterBar from "./KnowledgeFilterBar.jsx";
import {
  DATE_OPTIONS,
  EMPTY_FILTERS,
  applyFilters,
  countActive,
  getItemDateISO,
} from "../data/KnowledgeFilters.js";

const PAGE_SIZE = 9;

/* ----------------------------- breadcrumb ----------------------------- */
function Breadcrumb({ label }) {
  return (
    <nav
      aria-label="Breadcrumb"
      className="breadcrumb-in absolute left-4 top-4 z-20 sm:left-8 sm:top-6"
    >
      <ol className="flex items-center gap-2 rounded-full border border-black/10 bg-white/70 px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#141419] shadow-lg shadow-black/5 backdrop-blur-md sm:text-sm">
        <li>
          <a
            href="/"
            className="inline-flex items-center gap-1.5 transition-colors duration-300 hover:text-[#DA2128]"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 10.5 12 3l9 7.5" />
              <path d="M5 10v10h14V10" />
              <path d="M9 20v-6h6v6" />
            </svg>
            Home
          </a>
        </li>
        <li className="text-black/30">&rsaquo;</li>
        <li>
          <a href="/knowledge" className="transition-colors duration-300 hover:text-[#DA2128]">
            Knowledge
          </a>
        </li>
        <li className="text-black/30">&rsaquo;</li>
        <li className="rounded-full bg-[#DA2128] px-3 py-1 text-white shadow-sm">{label}</li>
      </ol>
    </nav>
  );
}

/* ----------------------------- pagination ---------------------------- */
function Pagination({ page, total, onChange }) {
  if (total <= 1) return null;
  return (
    <div className="mt-10 flex items-center justify-center gap-2">
      <button
        disabled={page === 1}
        onClick={() => onChange(page - 1)}
        className="w-10 h-10 rounded-xl grid place-items-center ring-1 ring-ink/15 text-ink/70 hover:text-brand hover:ring-brand disabled:opacity-35 disabled:pointer-events-none transition-all"
        aria-label="Previous page"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          onClick={() => onChange(n)}
          className={`relative w-10 h-10 rounded-xl text-[13px] font-extrabold transition-all ${
            n === page ? "text-white" : "ring-1 ring-ink/15 text-ink/70 hover:text-brand hover:ring-brand"
          }`}
        >
          {n === page && (
            <motion.span
              layoutId="knowledge-page-pill"
              className="absolute inset-0 rounded-xl bg-gradient-to-br from-crimson to-deeprose shadow-lg shadow-brand/30"
              transition={{ type: "spring", stiffness: 420, damping: 32 }}
            />
          )}
          <span className="relative">{n}</span>
        </button>
      ))}
      <button
        disabled={page === total}
        onClick={() => onChange(page + 1)}
        className="w-10 h-10 rounded-xl grid place-items-center ring-1 ring-ink/15 text-ink/70 hover:text-brand hover:ring-brand disabled:opacity-35 disabled:pointer-events-none transition-all"
        aria-label="Next page"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

/* Shared listing layout used by the three knowledge pages.
   Renders the image banner + title hero, then a filterable grid of
   clickable banner cards. Clicking a card calls onOpen(item). */
export default function KnowledgeSection({ section, items, onOpen }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const resultsRef = useRef(null);

  const filteredItems = useMemo(() => {
    const out = applyFilters(items, filters);
    out.sort((a, b) => {
      const da = getItemDateISO(a) || "";
      const db = getItemDateISO(b) || "";
      return sort === "newest" ? db.localeCompare(da) : da.localeCompare(db);
    });
    return out;
  }, [items, filters, sort]);

  useEffect(() => setPage(1), [filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const clampedPage = Math.min(page, totalPages);
  const start = (clampedPage - 1) * PAGE_SIZE;
  const pageItems = filteredItems.slice(start, start + PAGE_SIZE);

  const active = countActive(filters);

  const scrollToResults = () =>
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const changePage = (n) => {
    setPage(n);
    scrollToResults();
  };

  // active filter chips
  const chips = [
    filters.keyword.trim() && {
      key: "kw",
      label: `"${filters.keyword.trim()}"`,
      clear: () => setFilters((f) => ({ ...f, keyword: "" })),
    },
    ...filters.categories.map((c) => ({
      key: `c-${c}`,
      label: c,
      clear: () => setFilters((f) => ({ ...f, categories: f.categories.filter((v) => v !== c) })),
    })),
    ...filters.tags.map((t) => ({
      key: `t-${t}`,
      label: t,
      clear: () => setFilters((f) => ({ ...f, tags: f.tags.filter((v) => v !== t) })),
    })),
    filters.date && {
      key: "d",
      label: DATE_OPTIONS.find((o) => o.value === filters.date)?.label ?? filters.date,
      clear: () => setFilters((f) => ({ ...f, date: "" })),
    },
  ].filter(Boolean);

  return (
    <div className="bg-paper">
      {/* Hero banner with title */}
      <header id="top" className="relative overflow-hidden bg-[#FAF7F2]">
        <div className="hero-grid absolute inset-0" />
        <div className="pointer-events-none absolute -top-32 left-1/2 h-[360px] w-[680px] -translate-x-1/2 rounded-full bg-[#DA2128]/10 blur-[130px]" />
        <div className="pointer-events-none absolute -bottom-40 -right-24 h-[320px] w-[320px] rounded-full bg-[#DA2128]/[0.07] blur-[110px]" />

        <Breadcrumb label={section.label} />

        <div className="relative mx-auto flex max-w-7xl flex-col items-center justify-center px-4 pb-16 pt-28 text-center sm:px-6 sm:pt-32 lg:px-8">
          <div className="max-w-4xl">
            {/* <h1 className="font-display text-4xl font-bold leading-[1.05] text-ink drop-shadow-sm sm:text-2xl lg:text-2xl">
              {section.label}
            </h1>
            <p className="mt-4 text-lg text-ink/80">{section.tagline}</p>
            <p className="mt-3 max-w-3xl mx-auto text-sm leading-relaxed text-ink/70">
              {section.description}
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <button
                onClick={() => onOpen?.(items[0])}
                className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-cream/80 px-6 py-3.5 text-sm font-bold text-ink backdrop-blur-sm transition-all hover:bg-cream hover:scale-105"
              >
                <Icon name="download" className="h-4 w-4" />
                Latest Release
              </button>
              <a
                href="#library"
                className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-cream/80 px-6 py-3.5 text-sm font-bold text-ink backdrop-blur-sm transition-all hover:bg-cream hover:scale-105"
              >
                Browse Library
                <Icon name="arrow" className="h-4 w-4" />
              </a>
            </div> */}
          </div>
        </div>

        <div className="shimmer-line h-px w-full" />
      </header>

      {/* Library grid */}
      <section id="library" className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
          <div>
            <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-ember">
              <span className="h-2 w-2 rounded-full bg-ember animate-pulse-dot" />
              The Library
            </p>
            <h2 className="mt-2 font-display text-3xl font-bold text-ink">
              {section.short} Collection
            </h2>
          </div>
        </div>

        {/* filter bar */}
        <div className="mb-8 rounded-2xl bg-white ring-1 ring-brand/10 shadow-[0_10px_40px_-24px_rgba(180,35,24,0.35)] px-4 sm:px-5 py-4">
          <KnowledgeFilterBar
            items={items}
            filters={filters}
            setFilters={setFilters}
            onSearch={scrollToResults}
          />
        </div>

        <div ref={resultsRef} className="scroll-mt-32">
          {/* sort + count row */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-5 border-b-2 border-ink">
            <p className="text-[13px] font-semibold text-mute">
              {filteredItems.length === 0
                ? "No matching results"
                : `Showing ${start + 1}–${Math.min(start + PAGE_SIZE, filteredItems.length)} of ${filteredItems.length}`}
            </p>

            <div className="flex items-center gap-2">
              <ArrowDownWideNarrow className="w-4 h-4 text-mute" />
              <div className="flex rounded-xl ring-1 ring-ink/15 overflow-hidden text-[12px] font-extrabold">
                {[
                  ["newest", "Newest first"],
                  ["oldest", "Oldest first"],
                ].map(([v, l]) => (
                  <button
                    key={v}
                    onClick={() => setSort(v)}
                    className={`px-3.5 py-2 transition-colors ${
                      sort === v
                        ? "bg-gradient-to-r from-crimson to-deeprose text-white"
                        : "bg-white text-ink/60 hover:text-brand"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* active chips */}
          <AnimatePresence>
            {chips.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="flex flex-wrap items-center gap-2 py-4">
                  <span className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-mute mr-1">
                    Active filters —
                  </span>
                  {chips.map((c) => (
                    <motion.span
                      key={c.key}
                      layout
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.85 }}
                      className="inline-flex items-center gap-1.5 rounded-full bg-white ring-1 ring-brand/25 pl-3 pr-1.5 py-1 text-[11.5px] font-bold text-deeprose shadow-sm"
                    >
                      {c.label}
                      <button
                        onClick={c.clear}
                        className="w-[18px] h-[18px] rounded-full grid place-items-center bg-blush text-brand hover:bg-brand hover:text-white transition-colors"
                      >
                        <X className="w-2.5 h-2.5" strokeWidth={3} />
                      </button>
                    </motion.span>
                  ))}
                  <button
                    onClick={() => setFilters(EMPTY_FILTERS)}
                    className="text-[11.5px] font-extrabold text-brand underline underline-offset-4 decoration-ember hover:text-deeprose transition-colors ml-1"
                  >
                    Clear all
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* grid */}
          {pageItems.length > 0 ? (
            <div className="grid gap-6 pt-6 sm:grid-cols-2 lg:grid-cols-3">
              <AnimatePresence mode="popLayout">
                {pageItems.map((item, i) => (
                  <BannerCard key={item.id} item={item} index={i} onOpen={onOpen} />
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="py-20 flex flex-col items-center text-center"
            >
              <div className="w-20 h-20 rounded-3xl bg-blush ring-1 ring-brand/15 grid place-items-center">
                <FileSearch2 className="w-9 h-9 text-brand" />
              </div>
              <h3 className="mt-6 font-display font-bold text-2xl text-ink">No results found</h3>
              <p className="mt-2 text-[13.5px] text-mute max-w-sm">
                Nothing matches your current filter combination. Try removing a tag, widening the
                date range, or clearing all filters.
              </p>
              <button
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="mt-6 rounded-xl bg-gradient-to-r from-crimson to-deeprose text-white text-[13px] font-extrabold px-6 py-3 shadow-lg shadow-brand/30 hover:shadow-brand/50 transition-all"
              >
                Clear all filters
              </button>
            </motion.div>
          )}

          <Pagination page={clampedPage} total={totalPages} onChange={changePage} />
        </div>
      </section>
    </div>
  );
}
